import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

/**
 * Sends signed-out visitors to the login screen.
 *
 * Used as a layout route, so everything nested under it is protected and nothing has to
 * remember to guard itself:
 *
 *   <Route element={<RequireAuth />}>
 *     <Route path="/" element={<ManageInvites />} />
 *   </Route>
 *
 * Being inside the route table is what keeps /login and /healthz reachable. A guard
 * rendered as a sibling of the routes would fire on every path, including /login, and
 * redirect the login page to itself.
 *
 * There is no loading state to wait for: the session is read synchronously out of
 * sessionStorage, so isAuthenticated is already correct on the first render and this cannot
 * flash a redirect at someone who is in fact signed in.
 */
export function RequireAuth() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    //  Carried as a query param rather than router state so it survives the full page
    //  navigation that requireToken() does on an expired session, and so both paths to the
    //  login screen look the same to it.
    const from = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?from=${from}`} replace />;
  }

  return <Outlet />;
}

export default RequireAuth;
